/**
 * StewardGapBanner — the proactive hook on TripDetail.
 *
 * Renders ONLY when the server returned an opener. No gap, no banner: a trip
 * with nothing worth saying gets silence, not a manufactured nudge. That
 * restraint is the feature, not a gap in it.
 */
import { Sparkles } from "lucide-react";
import type { StewardGap } from "@/lib/types";

interface Props {
  opener: string;
  chips: string[];
  gap: StewardGap | null;
  /**
   * Flights that moved since the last time she spoke. Rendered as ONE quiet
   * line, never as an alert: the traveler made this change on purpose, so the
   * right note is recognition, not a warning. Empty on every visit after the
   * first, so it acknowledges once and then gets out of the way.
   */
  changes?: string[];
  onOpen: (seedMessage?: string) => void;
}

/** "3 unplanned days in Anchorage before your flight home". */
export function gapHeadline(gap: StewardGap | null): string | null {
  if (!gap || gap.gapDays.length === 0) return null;
  const n = gap.gapDays.length;
  const where = gap.arrivalCity ? ` in ${gap.arrivalCity}` : "";
  return `${n} unplanned day${n === 1 ? "" : "s"}${where} before your flight home`;
}

/** "The outbound DFW→YVR now departs 08:21 instead of 10:45." — joined, once. */
export function noticedLine(changes: string[] | undefined): string | null {
  const lines = (changes ?? []).filter((l) => typeof l === "string" && l.trim().length > 0);
  // Three separate sentences about flights is a changelog, not a greeting. Past
  // two we say how many moved and let the opener itself carry the detail.
  if (lines.length === 0) return null;
  if (lines.length > 2) return `${lines.length} flight details changed — I've caught up.`;
  return lines.join(" ");
}

export default function StewardGapBanner({ opener, chips, gap, changes, onOpen }: Props) {
  const headline = gapHeadline(gap);
  const noticed = noticedLine(changes);

  return (
    <section className="rounded-xl border border-[hsl(var(--primary))]/25 bg-[hsl(var(--primary))]/[0.04] p-4">
      <div className="flex items-start gap-3">
        <Sparkles
          className="mt-0.5 h-4 w-4 shrink-0 text-[hsl(var(--primary))]"
          aria-hidden="true"
        />
        <div className="min-w-0 flex-1 space-y-2">
          <div className="space-y-1">
            <p className="text-[11px] font-medium uppercase tracking-wide text-[hsl(var(--primary))]">
              Your Steward
            </p>
            {headline && (
              <p className="text-sm font-semibold text-foreground">{headline}</p>
            )}
            {/* The acknowledgment sits ABOVE the opener because it is the reason
                the opener now reads differently — an answer to "does she know I
                changed that?" before the traveler has to wonder it. */}
            {noticed && (
              <p className="text-xs leading-snug text-muted-foreground">
                <span className="font-medium text-foreground">Noticed</span>
                <span aria-hidden="true"> · </span>
                {noticed}
              </p>
            )}
            <p className="text-sm leading-relaxed text-muted-foreground">{opener}</p>
          </div>

          <div className="flex flex-wrap gap-1.5 pt-0.5">
            {chips.slice(0, 4).map((chip) => (
              <button
                key={chip}
                type="button"
                onClick={() => onOpen(chip)}
                className="rounded-full border border-[hsl(var(--primary))]/30 bg-card px-3 py-1 text-xs font-medium text-foreground transition-colors hover:border-[hsl(var(--primary))]/60 motion-reduce:transition-none"
              >
                {chip}
              </button>
            ))}
            <button
              type="button"
              onClick={() => onOpen()}
              className="rounded-full px-3 py-1 text-xs font-medium text-[hsl(var(--primary))] underline-offset-2 hover:underline"
            >
              Plan with Steward
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
